interface ContentTypeConfig {
  type: string;

  hasDedicatedRenderer: boolean;
  skipInTaskOperations: boolean;
}

/**
 * A tool result the chat UI dispatches to a card of its own, picked by this
 * `type`. They all behave alike here: no task operation section already covers
 * them, so nothing is skipped there. Where the card ends up is not uniform — `email_draft` and
 * `confirm_action` are hoisted out of the timeline into the action-required
 * section, and the `email_draft` card itself renders nothing in place.
 */
const toolResult = (type: string): ContentTypeConfig => ({
  type,
  hasDedicatedRenderer: true,
  skipInTaskOperations: false,
});

const CONTENT_TYPE_REGISTRY: Record<string, ContentTypeConfig> = {
  chart: {
    type: "chart",
    hasDedicatedRenderer: true,
    skipInTaskOperations: false,
  },
  presentation: {
    type: "presentation",
    hasDedicatedRenderer: true,
    skipInTaskOperations: false,
  },
  report: {
    type: "report",
    hasDedicatedRenderer: true,
    skipInTaskOperations: false,
  },
  task_result: {
    type: "task_result",
    hasDedicatedRenderer: true,
    skipInTaskOperations: true, // Displayed via TaskOperationRenderer header
  },
  summary: {
    type: "summary",
    hasDedicatedRenderer: true,
    skipInTaskOperations: true, // Displayed via TaskOperationRenderer summary section
  },
  progress: {
    type: "progress",
    hasDedicatedRenderer: true,
    skipInTaskOperations: true, // Displayed via TaskOperationRenderer progress section
  },
  "ask-user": {
    type: "ask-user",
    hasDedicatedRenderer: true,
    skipInTaskOperations: false,
  },
  answer: {
    type: "answer",
    hasDedicatedRenderer: true,
    skipInTaskOperations: false,
  },
  image: {
    type: "image",
    hasDedicatedRenderer: true,
    skipInTaskOperations: false,
  },
  video: toolResult("video"),
  reasoning: {
    type: "reasoning",
    hasDedicatedRenderer: true,
    skipInTaskOperations: false,
  },
  scout: {
    type: "scout",
    hasDedicatedRenderer: true,
    skipInTaskOperations: false,
  },
  collaborate: {
    type: "collaborate",
    hasDedicatedRenderer: true,
    skipInTaskOperations: true, // Displayed via TaskOperationRenderer collaboration section
  },
  task_board: {
    type: "task_board",
    hasDedicatedRenderer: true,
    skipInTaskOperations: true, // Displayed via TaskOperationRenderer task board section
  },
  session_document: toolResult("session_document"),
  live_session_search_results: {
    type: "live_session_search_results",
    hasDedicatedRenderer: true,
    skipInTaskOperations: false,
  },
  live_session_load_results: {
    type: "live_session_load_results",
    hasDedicatedRenderer: true,
    skipInTaskOperations: false,
  },
  load_chat: {
    type: "load_chat",
    hasDedicatedRenderer: true,
    skipInTaskOperations: false,
  },

  crafter: toolResult("crafter"),
  skills: toolResult("skills"),
  memory: toolResult("memory"),
  calendar_events: toolResult("calendar_events"),
  calendar_list: toolResult("calendar_list"),
  freebusy: toolResult("freebusy"),
  connect_google: toolResult("connect_google"),
  google_setup_required: toolResult("google_setup_required"),
  email_list: toolResult("email_list"),
  email_modify_result: toolResult("email_modify_result"),
  email_thread: toolResult("email_thread"),
  email_draft: toolResult("email_draft"),
  contacts: toolResult("contacts"),
  slack_setup_required: toolResult("slack_setup_required"),
  connect_slack: toolResult("connect_slack"),
  slack_channels: toolResult("slack_channels"),
  slack_messages: toolResult("slack_messages"),
  slack_search_results: toolResult("slack_search_results"),
  slack_users: toolResult("slack_users"),
  notion_search_results: toolResult("notion_search_results"),
  notion_page: toolResult("notion_page"),
  notion_database_rows: toolResult("notion_database_rows"),
  notion_users: toolResult("notion_users"),
  notion_comments: toolResult("notion_comments"),
  notion_setup_required: toolResult("notion_setup_required"),
  drive_file_list: toolResult("drive_file_list"),
  drive_file: toolResult("drive_file"),
  linear_issues: toolResult("linear_issues"),
  linear_issue: toolResult("linear_issue"),
  linear_comments: toolResult("linear_comments"),
  linear_teams: toolResult("linear_teams"),
  linear_projects: toolResult("linear_projects"),
  linear_initiatives: toolResult("linear_initiatives"),
  linear_document: toolResult("linear_document"),
  linear_users: toolResult("linear_users"),
  linear_setup_required: toolResult("linear_setup_required"),
  github_issues: toolResult("github_issues"),
  github_issue: toolResult("github_issue"),
  github_pull_request: toolResult("github_pull_request"),
  github_comments: toolResult("github_comments"),
  github_repos: toolResult("github_repos"),
  github_users: toolResult("github_users"),
  github_discussions: toolResult("github_discussions"),
  github_discussion: toolResult("github_discussion"),
  github_ci_status: toolResult("github_ci_status"),
  github_setup_required: toolResult("github_setup_required"),
  action_list: toolResult("action_list"),
  action_detail: toolResult("action_detail"),
  label_list: toolResult("label_list"),
  asset_list: toolResult("asset_list"),
  confirm_action: toolResult("confirm_action"),
  mcp_result: toolResult("mcp_result"),
  integration_suggestion: toolResult("integration_suggestion"),
  user_interjection: toolResult("user_interjection"),
};

function getContentTypeConfig(type: string | undefined): ContentTypeConfig | undefined {
  if (!type) return undefined;
  return CONTENT_TYPE_REGISTRY[type];
}

export function isStructuredContentType(type: string | undefined): boolean {
  return getContentTypeConfig(type)?.hasDedicatedRenderer ?? false;
}

/**
 * Every content type this registry says has a card of its own.
 *
 * Exported so a test can hold the registry against the block dispatcher: a type
 * listed here that the dispatcher cannot route is an entry with no renderer
 * behind it.
 */
export function structuredContentTypes(): string[] {
  return Object.values(CONTENT_TYPE_REGISTRY)
    .filter((config) => config.hasDedicatedRenderer)
    .map((config) => config.type);
}

export function shouldSkipInTaskOperations(type: string | undefined): boolean {
  return getContentTypeConfig(type)?.skipInTaskOperations ?? false;
}
